import React, {
    useEffect,
    useRef,
    useState
} from "react";

import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
 import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    getPOHRailways,
    getPOHCurrentMonth,
    getPOHOptions,
    getPOHList,
    createPOH,
    updatePOH,
    deletePOH
} from "../../../services/pohService";
import PageHeader from "../../../utils/PageHeader";


// ============================================================
// INITIAL FORM
// ============================================================

const initialForm = {
    rail: "",
    pohtype: "AC",
    pohsubtype: "AC",

    noofcoaches: 0,

    Mech_LabourCost: 0,
    Mech_MaterialCost: 0,

    Elec_LabourCost: 0,
    Elec_MaterialCost: 0
};


// ============================================================
// COMPONENT
// ============================================================

export default function POHDataEntry() {

    const [form, setForm] = useState(initialForm);

    const [railways, setRailways] = useState([]);

    const [pohTypes, setPOHTypes] = useState([
        "AC",
        "Non AC"
    ]);

    const [pohSubTypes, setPOHSubTypes] = useState([
        "AC",
        "Non AC",
        "NMG",
        "SS II",
        "SS III",
        "SPL",
        "MEMU",
        "DEMU",
        "MEMU_TC"
    ]);

    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");

    const [rows, setRows] = useState([]);

    const [editing, setEditing] = useState(false);

    const [oldKey, setOldKey] = useState(null);

    const [loading, setLoading] = useState(false);


    // ========================================================
    // REFS FOR ENTER KEY
    // ========================================================

    const railwayRef = useRef(null);
    const typeRef = useRef(null);
    const subtypeRef = useRef(null);
    const coachesRef = useRef(null);

    const mechLabourRef = useRef(null);
    const mechMaterialRef = useRef(null);

    const elecLabourRef = useRef(null);
    const elecMaterialRef = useRef(null);

    const saveRef = useRef(null);


    // ========================================================
    // MONTH NAME
    // ========================================================

    const getMonthName = (value) => {

        if (!value) return "";

        if (
            typeof value === "string" &&
            isNaN(Number(value))
        ) {
            return value;
        }

        const monthNumber = Number(value);

        if (
            monthNumber < 1 ||
            monthNumber > 12
        ) {
            return value;
        }

        return new Date(
            2000,
            monthNumber - 1,
            1
        ).toLocaleString(
            "en-IN",
            {
                month: "long"
            }
        );

    };


    const monthName = getMonthName(month);


    // ========================================================
    // NUMBER
    // ========================================================

    const num = (value) => {

        const n = Number(value);

        return Number.isFinite(n)
            ? n
            : 0;

    };


    // ========================================================
    // TOTAL
    // ========================================================

    const formTotal =
        num(form.Mech_LabourCost) +
        num(form.Mech_MaterialCost) +
        num(form.Elec_LabourCost) +
        num(form.Elec_MaterialCost);


    // ========================================================
    // INITIAL LOAD
    // ========================================================

    useEffect(() => {

        loadInitialData();

    }, []);


    // ========================================================
    // INITIAL DATA
    // ========================================================

    const loadInitialData = async () => {

        try {

            setLoading(true);

            const [
                railwayRes,
                monthRes,
                optionRes
            ] = await Promise.all([
                getPOHRailways(),
                getPOHCurrentMonth(),
                getPOHOptions()
            ]);


            setRailways(
                railwayRes.data?.data || []
            );


            const monthData =
                monthRes.data?.data;

            if (monthData) {

                setMonth(monthData.month);
                setYear(monthData.year);

            }


            if (optionRes.data?.data) {

                setPOHTypes(
                    optionRes.data.data.pohTypes
                );

                setPOHSubTypes(
                    optionRes.data.data.pohSubTypes
                );

            }


        } catch (err) {

            console.error(
                "Initial load error:",
                err
            );

        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // LOAD LIST
    // ========================================================

    useEffect(() => {

        if (
            month === "" ||
            year === ""
        ) {
            return;
        }

        loadList();

    }, [month, year]);


    const loadList = async () => {

        try {

            const res = await getPOHList(
                month,
                year
            );

            setRows(
                res.data?.data || []
            );

        } catch (err) {

            console.error(
                "Load POH list error:",
                err
            );

        }

    };


    // ========================================================
    // FORM CHANGE
    // ========================================================

    const handleChange = (field) => (event) => {

        setForm(prev => ({
            ...prev,
            [field]: event.target.value
        }));

    };


    // ========================================================
    // ENTER KEY
    // ========================================================

    const handleEnter = (
        event,
        nextRef
    ) => {

        if (
            event.key === "Enter"
        ) {

            event.preventDefault();

            nextRef?.current?.focus();

        }

    };


    // ========================================================
    // SAVE
    // ========================================================

    const handleSave = async () => {

        if (!form.rail) {

            alert(
                "Please select Railway"
            );

            railwayRef.current?.focus();

            return;

        }


        if (!form.pohtype) {

            alert(
                "Please select POH Type"
            );

            typeRef.current?.focus();

            return;

        }


        if (!form.pohsubtype) {

            alert(
                "Please select POH Sub Type"
            );

            subtypeRef.current?.focus();

            return;

        }


        if (
            Number(form.noofcoaches) <= 0
        ) {

            alert(
                "Please enter No of Coaches"
            );

            coachesRef.current?.focus();

            return;

        }


        const data = {

            rail: form.rail,

            pohtype: form.pohtype,

            pohsubtype: form.pohsubtype,

            noofcoaches:
                num(form.noofcoaches),

            Mech_LabourCost:
                num(form.Mech_LabourCost),

            Mech_MaterialCost:
                num(form.Mech_MaterialCost),

            Elec_LabourCost:
                num(form.Elec_LabourCost),

            Elec_MaterialCost:
                num(form.Elec_MaterialCost),

            PMONTH: month,

            PYEAR: year,
            month,
            year

        };


        try {

            setLoading(true);


            if (editing) {

                await updatePOH(
                    oldKey.rail,
                    oldKey.pohtype,
                    oldKey.pohsubtype,
                    data
                );

                alert(
                    "POH entry updated successfully"
                );

            } else {

                await createPOH(data);

                alert(
                    "POH entry saved successfully"
                );

            }


            resetForm();

            await loadList();


        } catch (err) {

            console.error(err);

            alert(
                err.response?.data?.message ||
                "Unable to save POH entry"
            );

        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // EDIT
    // ========================================================

    const handleEdit = (row) => {

        setForm({

            rail: row.rail,

            pohtype: row.pohtype,

            pohsubtype: row.pohsubtype,

            noofcoaches:
                row.noofcoaches || 0,

            Mech_LabourCost:
                row.Mech_LabourCost || 0,

            Mech_MaterialCost:
                row.Mech_MaterialCost || 0,

            Elec_LabourCost:
                row.Elec_LabourCost || 0,

            Elec_MaterialCost:
                row.Elec_MaterialCost || 0

        });


        setOldKey({

            rail: row.rail,

            pohtype: row.pohtype,

            pohsubtype:
                row.pohsubtype

        });


        setEditing(true);


        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });

    };


    // ========================================================
    // DELETE
    // ========================================================

    const handleDelete = async (row) => {

        const confirmDelete =
            window.confirm(
                `Delete ${row.rail} - ${row.pohtype} - ${row.pohsubtype}?`
            );


        if (!confirmDelete) {
            return;
        }


        try {

            setLoading(true);


            await deletePOH({

                rail: row.rail,

                pohtype:
                    row.pohtype,

                pohsubtype:
                    row.pohsubtype,

                month,

                year

            });


            await loadList();


        } catch (err) {

            console.error(err);

            alert(
                err.response?.data?.message ||
                "Unable to delete entry"
            );

        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // RESET
    // ========================================================

    const resetForm = () => {

        setForm({

            ...initialForm,

            rail:
                railways.length > 0
                    ? railways[0].railway_code
                    : ""

        });

        setEditing(false);

        setOldKey(null);

    };


    // ========================================================
    // RAILWAY NAME
    // ========================================================

    const getRailwayName = (code) => {

        const railway =
            railways.find(
                item =>
                    item.railway_code === code
            );

        return railway
            ? railway.railway_name
            : code;

    };


    // ========================================================
    // GRAND TOTALS
    // ========================================================

    const grand = rows.reduce(
        (acc, row) => {

            acc.coaches +=
                num(row.noofcoaches);

            acc.mechLabour +=
                num(row.Mech_LabourCost);

            acc.mechMaterial +=
                num(row.Mech_MaterialCost);

            acc.elecLabour +=
                num(row.Elec_LabourCost);

            acc.elecMaterial +=
                num(row.Elec_MaterialCost);

            acc.total +=
                num(row.Total);

            return acc;

        },
        {
            coaches: 0,
            mechLabour: 0,
            mechMaterial: 0,
            elecLabour: 0,
            elecMaterial: 0,
            total: 0
        }
    );


    // ========================================================
    // RENDER
    // ========================================================

    return (

       <Box
      sx={{
        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >

            {/* =================================================
                HEADER
            ================================================= */}
 <PageHeader
        title=" POH Data Entry"
        subtitle={`Data entry status for the month of: ${monthName} - ${year}`}
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        statusChip={{
          label: loading
            ? "Loading..."
            : grand.total.toFixed(2) > 0
              ? "Grand Total : " + grand.total.toFixed(2)
              : "No Data",
          color: loading ? "warning" : grand.total.toFixed(2)> 0 ? "primary" : "default",
        }}
       // showRefresh
       // onRefresh={handleRefresh}
        // refreshing={loading}
      />
           


            {/* =================================================
                ENTRY FORM
            ================================================= */}

            <Paper
                elevation={2}
                sx={{
                    p: {
                        xs: 1.25,
                        sm: 2
                    },
                    mb: 2,
                    borderRadius: 2
                }}
            >

                {/* ---------------------------------------------
                    GROUP TITLE
                --------------------------------------------- */}

                <Typography
                    sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: "#a12904"
                    }}
                >
                    POH Entry
                </Typography>


                {/* ---------------------------------------------
                    ENTRY ROW
                --------------------------------------------- */}

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.25,
                        alignItems: "flex-end"
                    }}
                >

                    {/* Railway */}

                    <Box
                        sx={{
                            flex:
                                "1 1 150px",
                            minWidth: {
                                xs: "100%",
                                sm: "140px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            Railway
                        </Typography>

                        <Select
                            fullWidth
                            size="small"
                            value={form.rail}
                            inputRef={railwayRef}
                            onChange={handleChange("rail")}
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    typeRef
                                )
                            }
                        >

                            {railways.map(
                                railway => (

                                    <MenuItem
                                        key={
                                            railway.railway_code
                                        }
                                        value={
                                            railway.railway_code
                                        }
                                    >
                                        {railway.railway_code}
                                        {" - "}
                                        {railway.railway_name}
                                    </MenuItem>

                                )
                            )}

                        </Select>

                    </Box>


                    {/* POH Type */}

                    <Box
                        sx={{
                            flex:
                                "1 1 120px",
                            minWidth: {
                                xs: "100%",
                                sm: "110px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            POH Type
                        </Typography>

                        <Select
                            fullWidth
                            size="small"
                            value={
                                form.pohtype
                            }
                            inputRef={typeRef}
                            onChange={
                                handleChange(
                                    "pohtype"
                                )
                            }
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    subtypeRef
                                )
                            }
                        >

                            {pohTypes.map(
                                type => (

                                    <MenuItem
                                        key={type}
                                        value={type}
                                    >
                                        {type}
                                    </MenuItem>

                                )
                            )}

                        </Select>

                    </Box>


                    {/* POH Sub Type */}

                    <Box
                        sx={{
                            flex:
                                "1 1 130px",
                            minWidth: {
                                xs: "100%",
                                sm: "120px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            POH Sub Type
                        </Typography>

                        <Select
                            fullWidth
                            size="small"
                            value={
                                form.pohsubtype
                            }
                            inputRef={subtypeRef}
                            onChange={
                                handleChange(
                                    "pohsubtype"
                                )
                            }
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    coachesRef
                                )
                            }
                        >

                            {pohSubTypes.map(
                                type => (

                                    <MenuItem
                                        key={type}
                                        value={type}
                                    >
                                        {type}
                                    </MenuItem>

                                )
                            )}

                        </Select>

                    </Box>


                    {/* Coaches */}

                    <Box
                        sx={{
                            flex:
                                "1 1 100px",
                            minWidth: {
                                xs: "100%",
                                sm: "90px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            No of Coaches
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={
                                form.noofcoaches
                            }
                            inputRef={
                                coachesRef
                            }
                            onChange={
                                handleChange(
                                    "noofcoaches"
                                )
                            }
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    mechLabourRef
                                )
                            }
                        />

                    </Box>


                    {/* Mechanical Labour */}

                    <Box
                        sx={{
                            flex:
                                "1 1 125px",
                            minWidth: {
                                xs: "100%",
                                sm: "115px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            Mech. Labour Cost
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={
                                form.Mech_LabourCost
                            }
                            inputRef={
                                mechLabourRef
                            }
                            onChange={
                                handleChange(
                                    "Mech_LabourCost"
                                )
                            }
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    mechMaterialRef
                                )
                            }
                        />

                    </Box>


                    {/* Mechanical Material */}

                    <Box
                        sx={{
                            flex:
                                "1 1 125px",
                            minWidth: {
                                xs: "100%",
                                sm: "115px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            Mech. Material Cost
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={
                                form.Mech_MaterialCost
                            }
                            inputRef={
                                mechMaterialRef
                            }
                            onChange={
                                handleChange(
                                    "Mech_MaterialCost"
                                )
                            }
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    elecLabourRef
                                )
                            }
                        />

                    </Box>


                    {/* Electrical Labour */}

                    <Box
                        sx={{
                            flex:
                                "1 1 125px",
                            minWidth: {
                                xs: "100%",
                                sm: "115px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            Elec. Labour Cost
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={
                                form.Elec_LabourCost
                            }
                            inputRef={
                                elecLabourRef
                            }
                            onChange={
                                handleChange(
                                    "Elec_LabourCost"
                                )
                            }
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    elecMaterialRef
                                )
                            }
                        />

                    </Box>


                    {/* Electrical Material */}

                    <Box
                        sx={{
                            flex:
                                "1 1 125px",
                            minWidth: {
                                xs: "100%",
                                sm: "115px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            Elec. Material Cost
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={
                                form.Elec_MaterialCost
                            }
                            inputRef={
                                elecMaterialRef
                            }
                            onChange={
                                handleChange(
                                    "Elec_MaterialCost"
                                )
                            }
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    saveRef
                                )
                            }
                        />

                    </Box>


                    {/* Total */}

                    <Box
                        sx={{
                            flex:
                                "1 1 110px",
                            minWidth: {
                                xs: "100%",
                                sm: "100px"
                            }
                        }}
                    >

                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700
                            }}
                        >
                            Total
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            value={
                                formTotal.toFixed(2)
                            }
                            InputProps={{
                                readOnly: true
                            }}
                        />

                    </Box>


                    {/* Save */}

                    <Box
                        sx={{
                            flex:
                                "0 1 100px",
                            minWidth: {
                                xs: "100%",
                                sm: "90px"
                            }
                        }}
                    >

                        <Button
                            fullWidth
                            variant="contained"
                            color={
                                editing
                                    ? "primary"
                                    : "success"
                            }
                            ref={saveRef}
                            onClick={
                                handleSave
                            }
                            disabled={
                                loading
                            }
                            sx={{
                                height: "40px",
                                fontWeight: 700
                            }}
                        >
                            {editing
                                ? "Update"
                                : "Save"}
                        </Button>

                    </Box>

                </Box>


                {/* Cancel Edit */}

                {editing && (

                    <Button
                        size="small"
                        sx={{
                            mt: 1
                        }}
                        onClick={
                            resetForm
                        }
                    >
                        Cancel Edit
                    </Button>

                )}

            </Paper>


            {/* =================================================
                RAILWAY TOTAL
            ================================================= */}

            <Paper
                elevation={1}
                sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 2
                }}
            >

                <Typography
                    sx={{
                        fontWeight: 700,
                        mb: 1
                    }}
                >
                    Railway Total
                </Typography>


                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1
                    }}
                >

                    <SummaryBox
                        label="No of Coaches"
                        value={grand.coaches}
                    />

                    <SummaryBox
                        label="Mech. Labour"
                        value={grand.mechLabour}
                    />

                    <SummaryBox
                        label="Mech. Material"
                        value={grand.mechMaterial}
                    />

                    <SummaryBox
                        label="Elec. Labour"
                        value={grand.elecLabour}
                    />

                    <SummaryBox
                        label="Elec. Material"
                        value={grand.elecMaterial}
                    />

                    <SummaryBox
                        label="Total"
                        value={grand.total}
                    />

                </Box>

            </Paper>


            {/* =================================================
                DATA LIST
            ================================================= */}

            <Paper
                elevation={2}
                sx={{
                    p: {
                        xs: 1,
                        sm: 1.5
                    },
                    mb: 2,
                    borderRadius: 2
                }}
            >

                <Typography
                    sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        color: "#a12904"
                    }}
                >
                    POH Entries
                </Typography>


                {/* Desktop Header */}

                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex"
                        },
                        bgcolor: "#a12904",
                        color: "white",
                        p: 1,
                        fontWeight: 700,
                        borderRadius:
                            "6px 6px 0 0"
                    }}
                >

                    <HeaderCell
                        width="9%"
                        text="Railway"
                    />

                    <HeaderCell
                        width="9%"
                        text="POH Type"
                    />

                    <HeaderCell
                        width="11%"
                        text="Sub Type"
                    />

                    <HeaderCell
                        width="9%"
                        text="Coaches"
                    />

                    <HeaderCell
                        width="11%"
                        text="Mech Labour"
                    />

                    <HeaderCell
                        width="11%"
                        text="Mech Material"
                    />

                    <HeaderCell
                        width="11%"
                        text="Elec Labour"
                    />

                    <HeaderCell
                        width="11%"
                        text="Elec Material"
                    />

                    <HeaderCell
                        width="10%"
                        text="Total"
                    />

                    <HeaderCell
                        width="8%"
                        text="Action"
                    />

                </Box>


                {/* Rows */}

                {rows.length === 0 ? (

                    <Box
                        sx={{
                            p: 4,
                            textAlign: "center",
                            color: "text.secondary"
                        }}
                    >
                        No POH entries found
                    </Box>

                ) : (

                    rows.map(row => (

                        <React.Fragment
                            key={`${row.rail}-${row.pohtype}-${row.pohsubtype}-${row.PMONTH}-${row.PYEAR}`}
                        >

                            {/* Desktop */}

                            <Box
                                sx={{
                                    display: {
                                        xs: "none",
                                        md: "flex"
                                    },
                                    alignItems: "center",
                                    borderBottom:
                                        "1px solid #ddd",
                                    p: 0.75
                                }}
                            >

                                <DataCell
                                    width="9%"
                                    value={
                                        row.rail
                                    }
                                />

                                <DataCell
                                    width="9%"
                                    value={
                                        row.pohtype
                                    }
                                />

                                <DataCell
                                    width="11%"
                                    value={
                                        row.pohsubtype
                                    }
                                />

                                <DataCell
                                    width="9%"
                                    value={
                                        row.noofcoaches
                                    }
                                />

                                <DataCell
                                    width="11%"
                                    value={
                                        row.Mech_LabourCost
                                    }
                                />

                                <DataCell
                                    width="11%"
                                    value={
                                        row.Mech_MaterialCost
                                    }
                                />

                                <DataCell
                                    width="11%"
                                    value={
                                        row.Elec_LabourCost
                                    }
                                />

                                <DataCell
                                    width="11%"
                                    value={
                                        row.Elec_MaterialCost
                                    }
                                />

                                <DataCell
                                    width="10%"
                                    value={
                                        num(row.Total).toFixed(2)
                                    }
                                />

                                <Box
                                    sx={{
                                        width: "8%",
                                        display: "flex",
                                        justifyContent:
                                            "center"
                                    }}
                                >

                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() =>
                                            handleEdit(row)
                                        }
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                            handleDelete(row)
                                        }
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>

                                </Box>

                            </Box>


                            {/* Mobile Card */}

                            <Paper
                                elevation={1}
                                sx={{
                                    display: {
                                        xs: "block",
                                        md: "none"
                                    },
                                    p: 1.5,
                                    mb: 1,
                                    border:
                                        "1px solid #ddd",
                                    borderRadius: 2
                                }}
                            >

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent:
                                            "space-between",
                                        alignItems:
                                            "center",
                                        mb: 1
                                    }}
                                >

                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color:
                                                "#a12904"
                                        }}
                                    >
                                        {row.rail}
                                        {" - "}
                                        {row.pohtype}
                                        {" - "}
                                        {row.pohsubtype}
                                    </Typography>

                                    <Box>

                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() =>
                                                handleEdit(row)
                                            }
                                        >
                                            <EditIcon />
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                handleDelete(row)
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>

                                    </Box>

                                </Box>


                                <Divider
                                    sx={{
                                        mb: 1
                                    }}
                                />


                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1
                                    }}
                                >

                                    <MobileValue
                                        label="Coaches"
                                        value={
                                            row.noofcoaches
                                        }
                                    />

                                    <MobileValue
                                        label="Mech Labour"
                                        value={
                                            row.Mech_LabourCost
                                        }
                                    />

                                    <MobileValue
                                        label="Mech Material"
                                        value={
                                            row.Mech_MaterialCost
                                        }
                                    />

                                    <MobileValue
                                        label="Elec Labour"
                                        value={
                                            row.Elec_LabourCost
                                        }
                                    />

                                    <MobileValue
                                        label="Elec Material"
                                        value={
                                            row.Elec_MaterialCost
                                        }
                                    />

                                    <MobileValue
                                        label="Total"
                                        value={
                                            num(row.Total).toFixed(2)
                                        }
                                        strong
                                    />

                                </Box>

                            </Paper>

                        </React.Fragment>

                    ))

                )}

            </Paper>


            {/* =================================================
                GRAND TOTAL
            ================================================= */}

            <Card
                elevation={2}
                sx={{
                    borderRadius: 2,
                    overflow: "hidden"
                }}
            >

                <CardContent
                    sx={{
                        bgcolor: "#a12904",
                        color: "white",
                        p: {
                            xs: 1.25,
                            sm: 1.5
                        }
                    }}
                >

                    <Typography
                        sx={{
                            fontWeight: 700,
                            mb: 1
                        }}
                    >
                        Grand Total
                    </Typography>


                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1
                        }}
                    >

                        <GrandBox
                            label="No of Coaches"
                            value={grand.coaches}
                        />

                        <GrandBox
                            label="Mech. Labour"
                            value={grand.mechLabour}
                        />

                        <GrandBox
                            label="Mech. Material"
                            value={grand.mechMaterial}
                        />

                        <GrandBox
                            label="Elec. Labour"
                            value={grand.elecLabour}
                        />

                        <GrandBox
                            label="Elec. Material"
                            value={grand.elecMaterial}
                        />

                        <GrandBox
                            label="Total"
                            value={
                                grand.total.toFixed(2)
                            }
                        />

                    </Box>

                </CardContent>

            </Card>

        </Box>

    );

}


// ============================================================
// SUMMARY BOX
// ============================================================

function SummaryBox({
    label,
    value
}) {

    return (

        <Box
            sx={{
                flex: "1 1 130px",
                minWidth: {
                    xs: "45%",
                    sm: "120px"
                }
            }}
        >

            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    fontWeight: 700
                }}
            >
                {label}
            </Typography>

            <Box
                sx={{
                    border:
                        "1px solid #ccc",
                    bgcolor: "#f5f5f5",
                    px: 1,
                    py: 0.6,
                    borderRadius: 1,
                    minHeight: "32px",
                    boxSizing: "border-box"
                }}
            >
                {Number(value).toFixed(2)}
            </Box>

        </Box>

    );

}


// ============================================================
// GRAND BOX
// ============================================================

function GrandBox({
    label,
    value
}) {

    return (

        <Box
            sx={{
                flex: "1 1 130px",
                minWidth: {
                    xs: "45%",
                    sm: "120px"
                }
            }}
        >

            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    fontWeight: 700
                }}
            >
                {label}
            </Typography>

            <Box
                sx={{
                    bgcolor: "white",
                    color: "black",
                    px: 1,
                    py: 0.6,
                    borderRadius: 1,
                    minHeight: "32px",
                    boxSizing: "border-box"
                }}
            >
                {Number(value).toFixed(2)}
            </Box>

        </Box>

    );

}


// ============================================================
// HEADER CELL
// ============================================================

function HeaderCell({
    width,
    text
}) {

    return (

        <Box
            sx={{
                width,
                textAlign: "center",
                fontSize: "0.78rem",
                boxSizing: "border-box"
            }}
        >
            {text}
        </Box>

    );

}


// ============================================================
// DATA CELL
// ============================================================

function DataCell({
    width,
    value
}) {

    return (

        <Box
            sx={{
                width,
                textAlign: "center",
                fontSize: "0.8rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box"
            }}
        >
            {value}
        </Box>

    );

}


// ============================================================
// MOBILE VALUE
// ============================================================

function MobileValue({
    label,
    value,
    strong
}) {

    return (

        <Box
            sx={{
                flex: "1 1 130px",
                bgcolor:
                    strong
                        ? "#fff3e0"
                        : "#f7f7f7",
                p: 1,
                borderRadius: 1,
                border:
                    "1px solid #ddd"
            }}
        >

            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    fontWeight: 700
                }}
            >
                {label}
            </Typography>

            <Typography
                sx={{
                    fontWeight:
                        strong
                            ? 700
                            : 500
                }}
            >
                {value}
            </Typography>

        </Box>

    );

}